import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  input,
  effect,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileApiService } from '@angular-workspace/services';
import { SkeletonComponent } from '../../../shared/components/skeleton/skeleton.component';
import { FormatService } from '../../../shared/services/format.service';
import { parseFilePreviewPayload } from '../file-preview.helper';

export interface DetailsVersionFile {
  name: string;
  size?: string;
  isSelected?: boolean;
  lastModified?: string;
}

export interface DetailsPreviewRow {
  [key: string]: string | number | null | undefined;
}

interface ApiFileItem {
  key?: string;
  size?: number;
  isFile?: boolean;
  lastModified?: string;
}

@Component({
  selector: 'app-dataset-detail-details-tab',
  standalone: true,
  imports: [CommonModule, SkeletonComponent],
  templateUrl: './details-tab.component.html',
  styleUrl: './details-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatasetDetailDetailsTabComponent {
  private readonly fileApi = inject(FileApiService);
  private readonly formatService = inject(FormatService);

  readonly datasetId = input<string | null>(null);
  readonly license = input<string>('');

  readonly versionFiles = signal<DetailsVersionFile[]>([]);
  readonly selectedFileName = signal<string | null>(null);
  readonly previewHeaders = signal<string[]>([]);
  readonly previewEntries = signal<DetailsPreviewRow[]>([]);
  readonly listLoading = signal(false);
  readonly previewLoading = signal(false);
  readonly selectedFileSize = signal('');
  readonly selectedFileLastUpdated = signal('');

  readonly previewRowLimit = 10;

  readonly displayedEntries = computed(() => {
    const entries = this.previewEntries();
    const limit = this.previewRowLimit;
    return entries.length > limit ? entries.slice(0, limit) : entries;
  });

  readonly hasPreviewData = computed(
    () =>
      this.previewHeaders().length > 0 && this.previewEntries().length > 0,
  );

  private loadGeneration = 0;

  constructor() {
    effect(() => {
      const id = this.datasetId();
      const gen = ++this.loadGeneration;
      untracked(() => {
        void this.bootstrapForDatasetId(id, gen);
      });
    });
  }

  private async bootstrapForDatasetId(
    id: string | null,
    gen: number,
  ): Promise<void> {
    this.selectedFileName.set(null);
    this.previewHeaders.set([]);
    this.previewEntries.set([]);
    this.selectedFileSize.set('');
    this.selectedFileLastUpdated.set('');
    if (!id) {
      this.versionFiles.set([]);
      return;
    }
    await this.loadFilesFromApi(id, gen);
  }

  private async loadFilesFromApi(id: string, gen: number): Promise<void> {
    this.listLoading.set(true);
    try {
      const response = await this.fileApi.getFilesForPreview(id);
      if (gen !== this.loadGeneration) return;
      const apiFiles = this.extractFilesFromResponse(response?.data);
      if (apiFiles.length === 0) {
        this.versionFiles.set([]);
        return;
      }
      const filtered = apiFiles.filter((f) => f.isFile !== false);
      this.versionFiles.set(
        filtered.map((f, i) => ({
          name: f.key || `file_${i + 1}`,
          size:
            (f.size || 0) > 0 ? this.formatFileSize(f.size || 0) : undefined,
          lastModified: f.lastModified,
        })),
      );
    } catch {
      if (gen !== this.loadGeneration) return;
      this.versionFiles.set([]);
    } finally {
      if (gen === this.loadGeneration) this.listLoading.set(false);
    }
  }

  private extractFilesFromResponse(data: unknown): ApiFileItem[] {
    if (!data || typeof data !== 'object') return [];
    const d = data as {
      files?: ApiFileItem[];
      data?: { files?: ApiFileItem[] };
    };
    if (Array.isArray(d.files)) return d.files;
    const inner = d.data;
    if (
      inner &&
      typeof inner === 'object' &&
      Array.isArray((inner as { files?: ApiFileItem[] }).files)
    ) {
      return (inner as { files: ApiFileItem[] }).files;
    }
    return [];
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    );
  }

  async onFileSelect(file: DetailsVersionFile): Promise<void> {
    const id = this.datasetId();
    this.selectedFileName.set(file.name);
    this.previewHeaders.set([]);
    this.previewEntries.set([]);
    if (!id) return;
    this.previewLoading.set(true);
    try {
      await this.loadFileMetadata(id, file.name);
      const response = await this.fileApi.generatePreview(id, file.name);
      const parsed = parseFilePreviewPayload(response?.data);
      if (parsed.error) {
        this.previewHeaders.set([]);
        this.previewEntries.set([]);
      } else {
        this.previewHeaders.set(parsed.headers);
        this.previewEntries.set(parsed.rows as DetailsPreviewRow[]);
      }
    } catch {
      this.previewHeaders.set([]);
      this.previewEntries.set([]);
    } finally {
      this.previewLoading.set(false);
    }
  }

  private async loadFileMetadata(
    datasetId: string,
    key: string,
  ): Promise<void> {
    try {
      const res = await this.fileApi.getFileMetadata(datasetId, { key });
      const raw = res?.data as Record<string, unknown> | undefined;
      const inner =
        raw?.['success'] && raw?.['data'] != null
          ? (raw['data'] as Record<string, unknown>)
          : raw;
      const meta = inner;
      if (meta?.['size'] != null) {
        const rawSize = meta['size'];
        const bytes =
          typeof rawSize === 'string' ? parseInt(rawSize, 10) : (rawSize as number);
        this.selectedFileSize.set(
          !Number.isNaN(bytes) ? this.formatFileSize(bytes) : '',
        );
      } else {
        const vf = this.versionFiles().find((f) => f.name === key);
        this.selectedFileSize.set(vf?.size ?? '');
      }
      const lastMod = meta?.['lastModified'];
      if (lastMod && typeof lastMod === 'string') {
        this.selectedFileLastUpdated.set(
          this.formatService.formatDate(lastMod) || '',
        );
      } else {
        const vf = this.versionFiles().find((f) => f.name === key);
        this.selectedFileLastUpdated.set(
          vf?.lastModified
            ? this.formatService.formatDate(vf.lastModified) || vf.lastModified
            : '',
        );
      }
    } catch {
      const vf = this.versionFiles().find((f) => f.name === key);
      this.selectedFileSize.set(vf?.size ?? '');
      this.selectedFileLastUpdated.set(
        vf?.lastModified
          ? this.formatService.formatDate(vf.lastModified) || vf.lastModified
          : '',
      );
    }
  }

  getCellValue(entry: DetailsPreviewRow, header: string): string {
    const value = entry[header];
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }
}
